// Browser-safe content queries via the Payload REST API.
// Visitors only ever receive published documents (enforced by collection access control).
import type { Blog, Reference } from '@/types/database'
import { sortReferences, toBlog, toReference } from './mappers'

type Status = 'draft' | 'published'
type Locale = 'tr' | 'en'

async function find(collection: string, where: Record<string, string | undefined>, single = false, limit?: number) {
  const params = new URLSearchParams({ depth: '1', sort: '-createdAt' })
  if (single) params.set('limit', '1')
  else if (limit) params.set('limit', String(limit))
  else params.set('pagination', 'false')
  for (const [field, value] of Object.entries(where)) {
    if (value !== undefined) params.set(`where[${field}][equals]`, value)
  }

  const res = await fetch(`/api/${collection}?${params}`)
  if (!res.ok) throw new Error(`Error fetching ${collection}: ${res.status}`)
  const data = await res.json()
  return data.docs as any[]
}

export async function getBlogs(status?: Status, locale?: Locale, limit?: number): Promise<Blog[]> {
  return (await find('blogs', { status, locale }, false, limit)).map(toBlog)
}

export async function getBlogBySlug(slug: string, locale?: Locale): Promise<Blog> {
  const [doc] = await find('blogs', { slug, locale }, true)
  if (!doc) throw new Error('Error fetching blog: not found')
  return toBlog(doc)
}

export async function getReferences(status?: Status, locale?: Locale): Promise<Reference[]> {
  return sortReferences((await find('references', { status, locale })).map(toReference))
}

export async function getReferenceBySlug(slug: string, locale?: Locale): Promise<Reference> {
  const [doc] = await find('references', { slug, locale }, true)
  if (!doc) throw new Error('Error fetching reference: not found')
  return toReference(doc)
}

export async function getReferenceById(id: string, locale?: Locale): Promise<Reference> {
  const [doc] = await find('references', { id, locale }, true)
  if (!doc) throw new Error('Error fetching reference: not found')
  return toReference(doc)
}
